/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    ACTIVITY_PASS_PIN,
    CAMPING_PIN,
    DAY_USE_PIN,
    PERMITS_PIN,
    POI_PIN,
    REC_AREA_PIN,
    TICKET_FACILITY_PIN,
    TOURS_PIN,
    VENUE_OTHER_PIN,
    venuePins,
} from './map.constants';

import type { MapMarkerItem } from './map.types';

/**
 * Maps a marker's entity type (and, where relevant, subtype metadata) to the
 * Mapbox Studio sprite name. Falls back to `poi-pin` for unknown types.
 *
 * Special cases:
 * - `campground` with `campsite_type_of_use` all `'Day'` → `day-use-pin`
 * - `venuereservations*` uses the first `venue_tags` entry to select the
 *   venue subtype sprite (defaults to `venue-other-pin`)
 */
export function getMapIconName(
    item: Pick<MapMarkerItem, 'entity_type' | 'campsite_type_of_use' | 'venue_tags'>
): string {
    switch (item.entity_type) {
        case 'campground': {
            if (item.campsite_type_of_use) {
                const allDayUse = item.campsite_type_of_use.every(
                    (type) => type === 'Day'
                );
                return allDayUse ? DAY_USE_PIN : CAMPING_PIN;
            }
            return CAMPING_PIN;
        }
        case 'permit':
        case 'vehiclepermit':
        case 'treepermit':
            return PERMITS_PIN;
        case 'tour':
        case 'timedentry_tour':
            return TOURS_PIN;
        case 'ticketfacility':
        case 'timedentry':
            return TICKET_FACILITY_PIN;
        case 'recarea':
            return REC_AREA_PIN;
        case 'activitypass':
            return ACTIVITY_PASS_PIN;
        case 'venuereservations':
        case 'venuereservations_venue': {
            const tag = item.venue_tags?.[0];
            return tag ? venuePins[tag] || VENUE_OTHER_PIN : VENUE_OTHER_PIN;
        }
        default:
            return POI_PIN;
    }
}

// cSpell:ignore venuereservations, activitypass, recarea, timedentry, ticketfacility, treepermit, vehiclepermit
